<script lang="ts">
  import type { Store } from '../state/store.svelte';
  import {
    SPEED_MIN_DAYS_PER_SECOND,
    SPEED_MAX_DAYS_PER_SECOND,
    selectSpeedScale
  } from '../state/selectors';
  import { writePlaybackPref } from '../persist/playback-pref';

  // Bottom-centre floating transport (Story 1.5). Button order: Reset, Slow,
  // Play/Pause (dominant), Fast, Analytic Model info; plus a speed slider and a
  // data-mono readout. `ui` depends on `state` only: it dispatches commands to the
  // store and imports the cookie adapter from `persist`; it never imports `core`
  // (AD-2 layer table). The `days/sec` unit label is a readout-boundary conversion
  // (AD-5); `state` holds the raw `speedDaysPerSecond` number.
  let {
    store,
    announce
  }: { store: Store; announce?: (message: string) => void } = $props();

  // Speed control bounds (FR-10). The control never emits outside [0.1, 365]; the
  // reducer clamps as a backstop. Sourced from the canonical `core/ranges.ts`
  // THROUGH the `state` re-export (never a `core` import, AD-2 layer table), so the
  // control, the reducer, and the Story 5.1 share codec share ONE range source.
  const MIN_SPEED = SPEED_MIN_DAYS_PER_SECOND;
  const MAX_SPEED = SPEED_MAX_DAYS_PER_SECOND;
  const STEP_FACTOR = 2;

  const paused = $derived(store.state.paused);
  // `speed` is the Sun-normalized BASE (what the reducer/share store). `scale` turns
  // it into the ACTUAL days/sec for the current centre, so a planet centre (huge
  // periods) runs its orbits at a watchable rate; the Sun's scale is exactly 1, so
  // everything below is a no-op in the default view.
  const speed = $derived(store.state.speedDaysPerSecond);
  const scale = $derived(selectSpeedScale(store.state));
  const effectiveSpeed = $derived(speed * scale);
  const effectiveMin = $derived(MIN_SPEED * scale);
  const effectiveMax = $derived(MAX_SPEED * scale);
  const effectiveStep = $derived(0.1 * scale);

  // Presentation-only speed formatting (AD-5). Whole numbers show plain; <1000 keeps
  // one decimal; larger warps (planet centres reach hundreds of thousands of days/sec
  // because their periods really are that long) get a compact k/M suffix so the
  // readout stays legible. Values <=365 (every Sun-centre value) are byte-identical
  // to the prior formatter.
  function formatSpeed(n: number): string {
    if (n >= 1_000_000) {
      const m = n / 1_000_000;
      return `${m >= 10 ? Math.round(m) : Number(m.toFixed(1))}M`;
    }
    if (n >= 1000) return `${Math.round(n / 1000)}k`;
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  }

  const speedLabel = $derived(`${formatSpeed(effectiveSpeed)} days/sec`);
  const stateLabel = $derived(paused ? 'Paused' : 'Playing');

  // Invisible, read-only probe of the raw SI simulation clock (state.time), exposed
  // so an e2e can prove AC 5 end-to-end: a higher speed advances the sim FURTHER
  // over the same real-time window. It is NOT a user-facing readout (never rendered
  // as text), so AD-5's "convert only at the ui boundary" does not apply; ui still
  // reads via state only. `String(...)` keeps a numeric attribute type-clean.
  const simTimeProbe = $derived(String(store.state.time));

  function clampSpeed(n: number): number {
    return Math.min(MAX_SPEED, Math.max(MIN_SPEED, n));
  }

  // `n` is BASE days/sec; the announcement (like the readout) reports the ACTUAL warp.
  function announceSpeed(baseN: number): void {
    announce?.(`Speed ${formatSpeed(baseN * scale)} simulated days per real second.`);
  }

  // The ONLY cookie-writing site (AC 6): the explicit Play/Pause user action writes
  // the preference in the same handler as the dispatch. Startup and Reset never
  // write, so a stored preference survives Reset (AC 9).
  function togglePlay(): void {
    if (paused) {
      store.dispatch({ type: 'Play' });
      writePlaybackPref('play');
      announce?.('Playing.');
    } else {
      store.dispatch({ type: 'Pause' });
      writePlaybackPref('pause');
      announce?.('Paused.');
    }
  }

  function setSpeed(next: number): void {
    const clamped = clampSpeed(next);
    store.dispatch({ type: 'SetSpeed', daysPerSecond: clamped });
    announceSpeed(clamped);
  }

  function onSpeedInput(event: Event): void {
    // The slider is in ACTUAL days/sec; convert back to the stored base before dispatch.
    const raw = Number((event.currentTarget as HTMLInputElement).value);
    if (!Number.isFinite(raw) || !(scale > 0)) return;
    setSpeed(raw / scale);
  }

  function slower(): void {
    setSpeed(speed / STEP_FACTOR);
  }

  function faster(): void {
    setSpeed(speed * STEP_FACTOR);
  }

  // Reset dispatches Reset only; it does NOT read or write the cookie (AC 9). Calm
  // recovery action, never destructive.
  function reset(): void {
    store.dispatch({ type: 'Reset' });
    announce?.('Simulation reset to the default solar system.');
  }
</script>

<section
  class="playback-hud glass-panel"
  aria-label="Playback controls"
  data-testid="playback-hud"
  data-sim-time={simTimeProbe}
>
  <button
    type="button"
    class="hud-btn"
    data-testid="reset"
    aria-label="Reset the simulation to the default solar system"
    onclick={reset}
  >
    <span class="material-symbols-outlined" aria-hidden="true">restart_alt</span>
    <span class="btn-label">Reset</span>
  </button>

  <div class="speed-group">
    <button
      type="button"
      class="hud-btn"
      data-testid="speed-slow"
      aria-label="Slow down"
      onclick={slower}
    >
      <span class="material-symbols-outlined" aria-hidden="true">fast_rewind</span>
      <span class="btn-label">Slow</span>
    </button>

    <div class="speed-slider-wrap">
      <label class="visually-hidden" for="speed-slider">
        Playback speed in simulated days per real second
      </label>
      <input
        id="speed-slider"
        class="speed-slider"
        type="range"
        min={effectiveMin}
        max={effectiveMax}
        step={effectiveStep}
        value={effectiveSpeed}
        aria-valuetext={`${formatSpeed(effectiveSpeed)} simulated days per real second`}
        data-testid="speed-slider"
        oninput={onSpeedInput}
      />
      <span class="speed-readout data-mono" data-testid="playback-readout">
        {speedLabel} • {stateLabel}
      </span>
    </div>

    <button
      type="button"
      class="hud-btn"
      data-testid="speed-fast"
      aria-label="Speed up"
      onclick={faster}
    >
      <span class="material-symbols-outlined" aria-hidden="true">fast_forward</span>
      <span class="btn-label">Fast</span>
    </button>
  </div>

  <button
    type="button"
    class="hud-btn play-pause"
    class:playing={!paused}
    data-testid="play-pause"
    data-playing={!paused}
    aria-pressed={!paused}
    aria-label={paused ? 'Play' : 'Pause'}
    onclick={togglePlay}
  >
    <span class="material-symbols-outlined play-icon" aria-hidden="true">
      {paused ? 'play_arrow' : 'pause'}
    </span>
    <span class="btn-label">{paused ? 'Play' : 'Pause'}</span>
  </button>
</section>

<style>
  .playback-hud {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: var(--control-gap);
    height: var(--playback-hud-height);
    padding: 0 20px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: var(--radius-xl);
    border-top-right-radius: var(--radius-xl);
    z-index: 14;
  }

  .hud-btn {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 48px;
    min-height: 48px;
    padding: 4px 8px;
    border: none;
    border-radius: var(--radius-default);
    background: transparent;
    color: var(--on-surface);
    cursor: pointer;
  }

  .hud-btn:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .btn-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  .speed-group {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .speed-slider-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 180px;
  }

  .speed-slider {
    width: 100%;
    accent-color: var(--primary);
    cursor: pointer;
  }

  .speed-readout {
    color: var(--on-surface-variant);
    white-space: nowrap;
  }

  /* Play/Pause is the dominant control: filled primary with a soft pulsing glow
     while paused (invites Play). Play vs Pause is distinguished by the filled icon
     and the label, never colour alone (AD-8). */
  .play-pause {
    min-width: 64px;
    min-height: 56px;
    border-radius: var(--radius-full);
    background: var(--primary-container);
    color: var(--on-primary-container);
    box-shadow: 0 0 0 0 rgba(164, 201, 255, 0.6);
    animation: primary-action-glow 2.2s ease-in-out infinite;
  }

  .play-pause:hover {
    background: var(--primary);
  }

  .play-pause.playing {
    /* Running: steady, no invitation glow. */
    animation: none;
  }

  .play-icon {
    font-size: 28px;
  }

  @keyframes primary-action-glow {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(164, 201, 255, 0.55);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(164, 201, 255, 0);
    }
  }

  /* Reduced motion: drop the glow/pulse; the icon and label state persist (AD-8,
     Reduced-motion convention). global.css also neutralises animation durations. */
  @media (prefers-reduced-motion: reduce) {
    .play-pause {
      animation: none;
    }
  }
</style>
